import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Button,
  Input,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  useBreakpointValue,
  useToast,
  Switch,
  Divider,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiRefreshCw,
  FiAlertTriangle,
  FiUsers,
  FiArrowLeft,
  FiEye,
} from 'react-icons/fi';
import {
  listPedagogicalExits,
  upsertPedagogicalExit,
  deletePedagogicalExit,
} from 'services/qrService';
import { buildNormalizedGroupOptions, getUserGroups } from 'utils/courseUtils';
import StaffPicker from 'components/Search/StaffPicker';
import ExitTargetsFields, { groupRawNames } from 'components/PedagogicalExit/ExitTargetsFields';
import PedagogicalExitDetailModal from './PedagogicalExitDetailModal';

const EMPTY_FORM = {
  id: null,
  title: '',
  group_names: [],
  students: [], // alumnos individuales — [{ id, name, username, group }]
  starts_at: '',
  ends_at: '',
  responsible_name: null, // persona elegida vía StaffPicker — { name, username?, ... } o null
  authorized_by: null,
  active: true,
};

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const PedagogicalExitsTab = () => {
  const [exits, setExits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [courseOptions, setCourseOptions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);

  // Modal más ancho en desktop para aprovechar 2 columnas — en mobile se
  // queda angosto y todo el contenido sigue apilado en una sola columna.
  const formModalSize = useBreakpointValue({ base: 'md', md: '3xl', xl: '5xl' });

  const { isOpen: isFormOpen, onOpen: onOpenForm, onClose: onCloseForm } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onOpenDetail, onClose: onCloseDetail } = useDisclosure();

  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const rowHover = useColorModeValue('gray.50', 'gray.700');

  const fetchExits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPedagogicalExits();
      setExits(res.data ?? []);
    } catch {
      setError('No se pudo cargar la lista de salidas pedagógicas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExits(); }, [fetchExits]);

  // Carga el glosario de cursos (mismo diccionario que Alumnos) para el selector
  useEffect(() => {
    (async () => {
      try {
        const rawGroups = await getUserGroups();
        setCourseOptions(buildNormalizedGroupOptions(rawGroups));
      } catch {
        // Sin glosario disponible — el selector quedará vacío
      }
    })();
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    onOpenForm();
  };

  const openEdit = (exit) => {
    setForm({
      id: exit.id,
      title: exit.title,
      group_names: exit.group_names ?? [],
      students: (exit.student_ids ?? []).map(s => ({ id: s.id, name: s.name, username: s.username })),
      starts_at: toDatetimeLocal(exit.starts_at),
      ends_at: toDatetimeLocal(exit.ends_at),
      // El registro solo guarda el nombre (string) — se sintetiza un objeto
      // mínimo para que el StaffPicker lo muestre como "seleccionado"; si el
      // admin quiere reemplazarlo por un usuario real, usa "Cambiar" y busca.
      responsible_name: exit.responsible_name ? { name: exit.responsible_name } : null,
      authorized_by: exit.authorized_by ? { name: exit.authorized_by } : null,
      active: exit.active,
    });
    setEditing(exit.id);
    onOpenForm();
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast({ title: 'El título es requerido', status: 'warning', duration: 2500 });
      return;
    }
    if (form.group_names.length === 0 && form.students.length === 0) {
      toast({ title: 'Agrega al menos un curso o un alumno', status: 'warning', duration: 2500 });
      return;
    }
    if (!form.starts_at || !form.ends_at) {
      toast({ title: 'Define el rango de fechas', status: 'warning', duration: 2500 });
      return;
    }
    setSaving(true);
    try {
      await upsertPedagogicalExit({
        id: form.id,
        title: form.title.trim(),
        group_names: form.group_names,
        student_ids: form.students.map(s => ({ id: s.id, name: s.name, username: s.username })),
        starts_at: form.starts_at,
        ends_at: form.ends_at,
        responsible_name: form.responsible_name?.name?.trim() || null,
        authorized_by: form.authorized_by?.name?.trim() || null,
        active: form.active,
      });
      toast({ title: editing ? 'Salida actualizada' : 'Salida creada', status: 'success', duration: 2500 });
      onCloseForm();
      fetchExits();
    } catch (e) {
      toast({
        title: 'Error al guardar',
        description: e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (exit) => {
    setDeleteTarget(exit);
    onOpenDelete();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.id);
    try {
      await deletePedagogicalExit(deleteTarget.id);
      toast({ title: 'Salida eliminada', status: 'success', duration: 2500 });
      onCloseDelete();
      fetchExits();
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    } finally {
      setDeleting(null);
    }
  };

  const setF = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  // "Ver detalle" — cursos/alumnos/fechas/responsable de la salida, más la(s)
  // ficha(s) firmada(s) si ya hay alguna (mismo FichaPreviewPanel que Perfil >
  // Fichas y el resumen del Scanner).
  const handleOpenDetail = (exit) => {
    setDetailTarget(exit);
    onOpenDetail();
  };

  return (
    <Box fontFamily="jakarta">
      <Flex justify="space-between" align="center" mb={4} gap="10px" flexWrap="wrap">
        <Box>
          <Text fontSize="18px" fontWeight="800" color="ink.900" letterSpacing="-0.02em">Salidas Pedagógicas</Text>
          <Text fontSize="13px" color="ink.400" fontWeight="500" mt="2px">
            {exits.length} salida{exits.length !== 1 ? 's' : ''} registrada{exits.length !== 1 ? 's' : ''} · {exits.filter(e => e.active).length} activa{exits.filter(e => e.active).length !== 1 ? 's' : ''}
          </Text>
        </Box>
        <HStack spacing="8px">
          <Tooltip label="Refrescar">
            <IconButton
              icon={<FiRefreshCw />}
              h="38px" w="38px" borderRadius="11px"
              border="1.5px solid" borderColor="cream.line2"
              bg="white" color="ink.700"
              _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
              onClick={fetchExits} isLoading={loading} aria-label="Refrescar"
            />
          </Tooltip>
          <Button
            h="38px" px={4} borderRadius="11px"
            bg="coral.500" color="#fff" fontWeight="700" fontSize="13px"
            _hover={{ bg: 'coral.600' }}
            boxShadow="0 8px 18px -8px rgba(239,90,42,.65)"
            leftIcon={<FiPlus />}
            onClick={openCreate}
          >
            Nueva salida
          </Button>
        </HStack>
      </Flex>

      <Box bg="white" borderRadius="22px" overflow="hidden" border="1px solid" borderColor="cream.line"
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)">
        {error && (
          <Alert status="error" m={4} borderRadius="10px">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <VStack py={12} spacing={3}>
            <Spinner size="lg" color="primary.500" />
            <Text fontSize="sm" color={mutedColor}>Cargando salidas pedagógicas…</Text>
          </VStack>
        ) : exits.length === 0 ? (
          <VStack py={12} spacing={2}>
            <Icon as={FiUsers} color={mutedColor} boxSize={8} />
            <Text color={mutedColor} fontSize="sm">Sin salidas pedagógicas registradas</Text>
          </VStack>
        ) : (
          <Box>
            {exits.map((exit, idx) => {
              const courses = groupRawNames(exit.group_names, courseOptions);
              return (
                <Box
                  key={exit.id}
                  borderTop={idx === 0 ? 'none' : '1px solid'}
                  borderColor="cream.line"
                  px={5} py={5}
                  _hover={{ bg: 'coral.tint' }} transition="background .13s"
                  opacity={exit.active ? 1 : 0.5}
                >
                  <Flex align="flex-start" gap="16px" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                    {/* Icono */}
                    <Box
                      w="44px" h="44px" borderRadius="13px" flexShrink={0}
                      bg="lav.bg" display="flex" alignItems="center" justifyContent="center"
                    >
                      <Icon as={FiArrowLeft} boxSize="22px" color="lav.ink" />
                    </Box>

                    {/* Contenido */}
                    <Box flex="1 1 200px" minW={0}>
                      <Text fontWeight="800" fontSize="15.5px" color="ink.900" letterSpacing="-0.01em">
                        {exit.title}
                      </Text>
                      <HStack spacing="8px" mt="3px" flexWrap="wrap">
                        <Text fontSize="13px" color="ink.700" fontWeight="500">
                          {formatDate(exit.starts_at)} → {formatDate(exit.ends_at)}
                        </Text>
                        {exit.authorized_by && (
                          <>
                            <Text fontSize="13px" color="ink.400">·</Text>
                            <Text fontSize="13px" color="ink.400" fontWeight="500">
                              Autorizado por {exit.authorized_by}
                            </Text>
                          </>
                        )}
                      </HStack>
                      {/* Chips de cursos */}
                      <HStack mt="10px" spacing="6px" flexWrap="wrap">
                        {courses.map(c => (
                          <Box
                            key={c.code}
                            as="span" px="10px" py="4px" borderRadius="999px"
                            bg="lav.bg" color="lav.ink"
                            fontSize="11.5px" fontWeight="700"
                          >
                            {c.label}
                          </Box>
                        ))}
                        {courses.length > 0 && (
                          <Text fontSize="12.5px" color="ink.400" fontWeight="500">
                            {courses.length} curso{courses.length !== 1 ? 's' : ''}
                          </Text>
                        )}
                        {exit.student_ids?.length > 0 && (
                          <Box
                            as="span" px="10px" py="4px" borderRadius="999px"
                            bg="purple.50" color="purple.700"
                            fontSize="11.5px" fontWeight="700"
                          >
                            + {exit.student_ids.length} alumno{exit.student_ids.length !== 1 ? 's' : ''}
                          </Box>
                        )}
                      </HStack>
                    </Box>

                    {/* Acciones */}
                    <HStack spacing="6px" flexShrink={0} align="flex-start" ml={{ base: 'auto', md: 0 }}>
                      <Tooltip label="Ver detalle">
                        <IconButton
                          icon={<FiEye />} w="34px" h="34px" minW="34px" borderRadius="9px"
                          bg="cream.bg2" color="lav.ink" border="none"
                          _hover={{ bg: 'lav.bg' }}
                          onClick={() => handleOpenDetail(exit)}
                          aria-label="Ver detalle"
                        />
                      </Tooltip>
                      <Box
                        as="span" px="10px" py="4px" borderRadius="999px"
                        bg={exit.active ? '#DCF1E6' : 'cream.bg2'}
                        color={exit.active ? '#1E7A4E' : 'ink.400'}
                        fontSize="12px" fontWeight="700"
                      >
                        {exit.active ? 'Activo' : 'Inactivo'}
                      </Box>
                      <Tooltip label="Editar">
                        <IconButton
                          icon={<FiEdit3 />} w="34px" h="34px" minW="34px" borderRadius="9px"
                          bg="cream.bg2" color="ink.400" border="none"
                          _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                          onClick={() => openEdit(exit)} aria-label="Editar"
                        />
                      </Tooltip>
                      <Tooltip label="Eliminar">
                        <IconButton
                          icon={<FiTrash2 />} w="34px" h="34px" minW="34px" borderRadius="9px"
                          bg="cream.bg2" color="danger" border="none"
                          _hover={{ bg: '#FEE2E2' }}
                          onClick={() => confirmDelete(exit)}
                          isLoading={deleting === exit.id} aria-label="Eliminar"
                        />
                      </Tooltip>
                    </HStack>
                  </Flex>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ── Modal crear / editar ── */}
      <Modal isOpen={isFormOpen} onClose={onCloseForm} isCentered size={formModalSize} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={3} minH={{ md: '620px' }}>
          <ModalHeader>{editing ? 'Editar salida pedagógica' : 'Nueva salida pedagógica'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* 2 columnas en desktop (md+): izquierda = a quién cubre la
                salida, derecha = cuándo y quién la autoriza. En mobile se
                apila en una sola columna. */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="start">
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Título</FormLabel>
                  <Input size="sm" borderRadius="8px" placeholder="Ej: Salida a museo 4° Básico"
                    value={form.title} onChange={setF('title')} />
                </FormControl>

                <ExitTargetsFields
                  groupNames={form.group_names}
                  onGroupNamesChange={(group_names) => setForm(f => ({ ...f, group_names }))}
                  students={form.students}
                  onStudentsChange={(students) => setForm(f => ({ ...f, students }))}
                  courseOptions={courseOptions}
                />
              </VStack>

              <VStack spacing={4} align="stretch">
                <HStack spacing={3} align="flex-start">
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Desde</FormLabel>
                    <Input type="datetime-local" size="sm" borderRadius="8px"
                      value={form.starts_at} onChange={setF('starts_at')} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Hasta</FormLabel>
                    <Input type="datetime-local" size="sm" borderRadius="8px"
                      value={form.ends_at} onChange={setF('ends_at')} />
                  </FormControl>
                </HStack>

                {/* Mismo StaffPicker (buscador contra usuarios reales de
                    datofy-api) que ya usa el flujo del escáner QR para
                    encargado/quien-permite — antes esto era texto libre. */}
                <FormControl>
                  <FormLabel fontSize="sm">Responsable</FormLabel>
                  <StaffPicker
                    value={form.responsible_name}
                    onSelect={(person) => setForm(f => ({ ...f, responsible_name: person }))}
                    placeholder="Buscar responsable…"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Autorizado por</FormLabel>
                  <StaffPicker
                    value={form.authorized_by}
                    onSelect={(person) => setForm(f => ({ ...f, authorized_by: person }))}
                    placeholder="Buscar quien autoriza…"
                  />
                </FormControl>

                <Divider />

                <FormControl display="flex" alignItems="center" gap={3}>
                  <FormLabel mb={0} fontSize="sm">Activo</FormLabel>
                  <Switch isChecked={form.active}
                    onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                    colorScheme="secondary" />
                </FormControl>
              </VStack>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseForm}>Cancelar</Button>
            <Button variant="educational-primary" size="sm" onClick={handleSave} isLoading={saving}>
              {editing ? 'Actualizar' : 'Crear salida'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Modal eliminar ── */}
      <Modal isOpen={isDeleteOpen} onClose={onCloseDelete} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>Eliminar salida pedagógica</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} spacing={3}>
              <Icon as={FiAlertTriangle} color="red.500" boxSize={6} />
              <Text fontWeight="semibold">¿Eliminar «{deleteTarget?.title}»?</Text>
            </HStack>
            <Text fontSize="sm" color={mutedColor}>
              Esta acción no se puede deshacer.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseDelete}>Cancelar</Button>
            <Button colorScheme="red" size="sm" onClick={handleDelete} isLoading={!!deleting}>
              Sí, eliminar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <PedagogicalExitDetailModal
        isOpen={isDetailOpen}
        onClose={onCloseDetail}
        exit={detailTarget}
        courseOptions={courseOptions}
      />
    </Box>
  );
};

export default PedagogicalExitsTab;
